import React, { useState, useEffect, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { Popover, Overlay } from "react-bootstrap";
import { FiX } from "react-icons/fi";

export default function Sidebar({ title = "Asset Tracking", applications, activeApp, setActiveApp, devices, assetStateFilter, setAssetStateFilter }) {

  //#region state
  const [clickedDevice, setClickedDevice] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const targetRef = useRef(null);


  //#endregion

  //#region helpers
  const deriveDeviceState = (device) => {
    const latest = device?.latest;
    if (!latest) {
      return {
        state: "UNKNOWN",
        label: "Unknown",
        color: "#ccc",
        timestamp: null
      };
    }

    const ts = latest.at?.toDate
      ? latest.at.toDate()
      : new Date(latest.at?.seconds * 1000);

    // EVENTS (authoritative)
    if (latest.payloadType === "event") {
      if (latest.eventType === "Start of movement") {
        return {
          state: "MOVING",
          label: "Started moving",
          color: "#81f6a4ff",
          timestamp: ts
        };
      }

      if (latest.eventType === "End of movement") {
        return {
          state: "IDLE",
          label: "Stopped moving",
          color: "#f4a3d9ff",
          timestamp: ts
        };
      }
    }

    // LOCATION FIXES
    if (latest.payloadType === "location_fixed") {
      if (latest.stillInMotion) {
        return {
          state: "MOVING",
          label: "Moving",
          color: "#81f6a4ff",
          timestamp: ts
        };
      }

      return {
        state: "IDLE",
        label: "Stationary (position fixed)",
        color: "#a3d1f4",
        timestamp: ts
      };
    }

    if (latest.payloadType === "location_failure") {
      return {
        state: "NO_FIX",
        label: "Position failed",
        color: "#f6a5a5ff",
        timestamp: ts
      };
    }

    if (latest.payloadType === "heartbeat") {
      return {
        state: "IDLE",
        label: "Device OK",
        color: "#a5f8beff",
        timestamp: ts
      };
    }

    if (latest.payloadType === "shutdown") {
      return {
        state: "OFFLINE",
        label: "Device shutdown",
        color: "#d3c9c9ff",
        timestamp: ts
      };
    }

    return {
      state: "UNKNOWN",
      label: "Unknown",
      color: "#ccc",
      timestamp: ts
    };
  };
  //#endregion

  //#region handlers
  const handleDeviceClick = (e, deviceEui, d) => {
    setClickedDevice(deviceEui);
    targetRef.current = e.target;
    setShowPopup((prev) => !prev);
    console.log('device ', d)
  };

  // derived data for popup
  const selectedDevice = clickedDevice
    ? devices.find(d => d.meta.deviceEui === clickedDevice)
    : null;

  const derived = selectedDevice
    ? deriveDeviceState(selectedDevice)
    : null;

  const lastValidPosTime = selectedDevice?.lastValidPosition?.at
    ? selectedDevice.lastValidPosition.at.toDate()
    : null;

  const lastMotionTime = selectedDevice?.meta?.deviceLastMotionAt
    ? selectedDevice.meta.deviceLastMotionAt.toDate()
    : null;

  //#endregion
  return (
    <div
      className="border rounded flex-column p-3 border-end bg-light d-flex flex-column gap-4"
      style={{ width: 300, height: "100%", position: "sticky", top: 0 }}
    >
      {/* Sidebar header */}
      <div className="fs-5 fw-semibold p-2 border-bottom text-center">
        {title}
      </div>

      <div className="menu-section d-flex flex-column gap-4">

        {/* Application filter */}
        <div className="application-filter">

          {/* Filter label */}
          <div className="text-muted small fw-semibold mb-2">Filter by Assets</div>
          <hr className="mt-0 mb-2" />

          {/* Dropdown for applications */}
          {Object.keys(applications)?.length === 0 || !applications ? (
            <div className="text-center text-muted">No Assets found.</div>
          ) : (
            <Dropdown className="w-100" style={{ background: 'white' }}>
              <Dropdown.Toggle
                className="d-flex align-items-center justify-content-between shadow-sm border w-100 px-3 py-2"
                variant="light"
                style={{ background: '#fff' }}
              >
                {activeApp || "Select application"}
              </Dropdown.Toggle>
              <Dropdown.Menu className="w-100 p-0 overflow-hidden shadow" >
                {Object.keys(applications)?.map((appName) => (
                  <Dropdown.Item
                    key={appName}
                    active={appName === activeApp}
                    onClick={() => setActiveApp(appName)}
                  >
                    {appName}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          )}
        </div>

        {/* Asset State filter */}
        <div className="">
          <div className="text-muted small fw-semibold mb-2">Filter by Asset State</div>
          <hr className="mt-0 mb-2" />
          <Dropdown className="w-100" style={{ background: 'white' }}>
            <Dropdown.Toggle
              className="d-flex align-items-center justify-content-between shadow-sm border w-100 px-3 py-2"
              variant="light"
              style={{ background: '#fff' }}
            >
              {assetStateFilter || "All"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="w-100 p-0 overflow-hidden shadow" >
              {["All", "Active", "Idle"].map((type) => (
                <Dropdown.Item
                  key={type}
                  active={type === assetStateFilter}
                  onClick={() => setAssetStateFilter(type)}
                >
                  {type}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* Device list */}
        <div className="">
          <div className="text-muted small fw-semibold mb-2">Devices in {activeApp || "Unknown"}</div>
          <hr className="mt-0 mb-2" />

          {devices?.length === 0 || !devices ? (
            <div className="text-muted text-center small">No devices yet.</div>
          ) : (
            <>
              <ul className="deviceList small border rounded" style={{ height: '300px', width: '100%', overflowY: 'auto', background: 'white', listStyle: 'none', padding: '6px', margin: '0' }}>
                {devices.map((d, i) => {
                  const derived = deriveDeviceState(d);

                  return (
                    <li
                      key={d.meta.deviceEui}
                      style={{
                        background: derived.color,
                        cursor: "pointer",
                        padding: 6,
                        borderRadius: 6,
                        marginBottom: 4
                      }}
                      onClick={(e) => handleDeviceClick(e, d.meta.deviceEui)}
                    >
                      <div className="fw-semibold">
                        {d.meta.deviceName}
                      </div>

                      <div style={{ fontSize: "0.8em" }}>
                        {derived.label}
                      </div>

                      <div style={{ fontSize: "0.75em", opacity: 0.75 }}>
                        {derived.timestamp?.toLocaleString()}
                      </div>
                    </li>
                  );
                })}
              </ul>

              {/* overlay when device clicked */}
              <Overlay
                show={showPopup}
                target={targetRef.current}
                placement="right"
                containerPadding={20}
                rootClose
                onHide={() => setShowPopup(false)}
              >
                <Popover.Body className="small p-3 bg-white border shadow" style={{ width: 250 }}>
                  {selectedDevice && (
                    <>
                      <div className="fw-semibold mb-2">Device</div>

                      <div><strong>Name:</strong> {selectedDevice.meta.deviceName}</div>
                      <div><strong>State:</strong> {derived.label}</div>

                      <hr />

                      <div>
                        <strong>Last update:</strong><br />
                        {derived.timestamp?.toLocaleString() || "—"}
                      </div>

                      <div className="mt-2">
                        <strong>Last valid position:</strong><br />
                        {lastValidPosTime?.toLocaleString() || "—"}
                      </div>

                      <div className="mt-2">
                        <strong>Last movement:</strong><br />
                        {lastMotionTime?.toLocaleString() || "—"}
                      </div>

                      {selectedDevice.latest?.payloadType === 'location_failure' && selectedDevice.latest?.positioningFailureReason && (
                        <div className="mt-2 text-danger">
                          <strong>GPS reason:</strong><br />
                          {selectedDevice.latest.positioningFailureReason}
                        </div>
                      )}
                    </>
                  )}
                </Popover.Body>
              </Overlay>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
